import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  signal,
} from '@angular/core';
import {
  NonNullableFormBuilder,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatFormField, MatPrefix, MatSuffix } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatError, MatInput } from '@angular/material/input';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatSortModule, Sort } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { MatTooltip } from '@angular/material/tooltip';
import { EMPTY, catchError, finalize } from 'rxjs';
import { escapeHtml } from '../../utils/html';
import { DataTableContainerComponent } from '../../components/data-table-container/data-table-container.component';
import { DialogService } from '../../components/dialog/dialog.service';
import { HasPermissionDirective } from '@ipg/ngx-frontend-toolset';
import { TableLimitService } from '../../services/table-limit.service';
import { TagResponse, TagService } from '../../services/tag.service';

@Component({
  selector: 'app-settings-tags',
  imports: [
    MatButton,
    MatError,
    MatFormField,
    MatIcon,
    MatIconButton,
    MatInput,
    MatPaginator,
    MatPrefix,
    MatSuffix,
    MatSortModule,
    MatTableModule,
    MatTooltip,
    ReactiveFormsModule,
    DataTableContainerComponent,
    HasPermissionDirective,
  ],
  templateUrl: './settings-tags.component.html',
  styleUrl: './settings-tags.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SettingsTagsComponent {
  private readonly _dialog = inject(DialogService);
  private readonly _fb = inject(NonNullableFormBuilder);
  private readonly _snackbar = inject(MatSnackBar);
  private readonly _tagService = inject(TagService);
  private readonly _tableLimit = inject(TableLimitService);

  protected readonly tags = this._tagService.tags;
  protected readonly pendingDeletionTags = this._tagService.pendingDeletionTags;
  protected readonly inactiveTags = this._tagService.inactiveTags;
  protected readonly showAddForm = signal(false);
  protected readonly pendingExpanded = signal(false);
  protected readonly inactiveExpanded = signal(false);
  protected readonly submitting = signal(false);
  protected readonly keyword = signal<string | null>(null);
  protected readonly page = signal(0);
  protected readonly pageSize = signal(this._tableLimit.getLimit());

  protected readonly displayedColumns = ['label', 'description', 'actions'];
  protected readonly pendingDisplayedColumns = [
    'label',
    'description',
    'deleteAt',
    'actions',
  ];
  protected readonly inactiveDisplayedColumns = ['label', 'description', 'actions'];

  /** Currently editing tag id, or null */
  protected readonly editingTagId = signal<string | null>(null);

  /** Sort state for main table */
  protected readonly sortActive = signal<string>('label');
  protected readonly sortDirection = signal<'asc' | 'desc' | ''>('asc');

  /** Sort state for pending table */
  protected readonly pendingSortActive = signal<string>('');
  protected readonly pendingSortDirection = signal<'asc' | 'desc' | ''>('');

  /** Filtered tags based on search query, sorted by active sort */
  protected readonly filteredTags = computed(() => {
    const query = this.keyword()?.toLowerCase().trim();
    const allTags = this.tags();

    const filtered = query
      ? allTags.filter(
          (t) =>
            t.label.toLowerCase().includes(query) ||
            (t.description?.toLowerCase().includes(query) ?? false)
        )
      : allTags;

    return this._sortTags(filtered, this.sortActive(), this.sortDirection());
  });

  /** Paginated slice of filtered tags */
  protected readonly paginatedTags = computed(() => {
    const tags = this.filteredTags();
    const start = this.page() * this.pageSize();
    return tags.slice(start, start + this.pageSize());
  });

  /** Filtered inactive tags based on search query */
  protected readonly filteredInactiveTags = computed(() => {
    const query = this.keyword()?.toLowerCase().trim();
    const allTags = this.inactiveTags();

    return query
      ? allTags.filter(
          (t) =>
            t.label.toLowerCase().includes(query) ||
            (t.description?.toLowerCase().includes(query) ?? false)
        )
      : allTags;
  });

  /** Filtered pending-deletion tags based on search query */
  protected readonly filteredPendingTags = computed(() => {
    const query = this.keyword()?.toLowerCase().trim();
    const allTags = this.pendingDeletionTags();

    const filtered = query
      ? allTags.filter(
          (t) =>
            t.label.toLowerCase().includes(query) ||
            (t.description?.toLowerCase().includes(query) ?? false)
        )
      : allTags;

    return this._sortTags(
      filtered,
      this.pendingSortActive(),
      this.pendingSortDirection()
    );
  });

  protected readonly hasNoData = computed(() => this.tags().length === 0);

  protected readonly addForm = this._fb.group({
    label: this._fb.control('', [Validators.required, Validators.maxLength(100)]),
    description: this._fb.control('', [Validators.maxLength(255)]),
  });

  protected readonly editForm = this._fb.group({
    label: this._fb.control('', [Validators.required, Validators.maxLength(100)]),
    description: this._fb.control('', [Validators.maxLength(255)]),
  });

  constructor() {
    this._tagService.getAll().subscribe();
    this._tagService.getPendingDeletionTags().subscribe();
    this._tagService.getInactiveTags().subscribe();
  }

  openAddForm() {
    this.addForm.reset();
    this.showAddForm.set(true);
    this.editingTagId.set(null);
  }

  cancelAdd() {
    this.showAddForm.set(false);
  }

  startEdit(t: TagResponse) {
    this.showAddForm.set(false);
    this.editingTagId.set(t.id);
    this.editForm.patchValue({
      label: t.label,
      description: t.description ?? '',
    });
  }

  cancelEdit() {
    this.editingTagId.set(null);
  }

  handleSearch(value: string) {
    this.keyword.set(value.trim() || null);
    this.page.set(0);
  }

  clearSearch() {
    this.keyword.set(null);
    this.page.set(0);
  }

  handlePageChange({ pageIndex, pageSize }: PageEvent) {
    this._tableLimit.saveLimit(pageSize);
    this.pageSize.set(pageSize);
    this.page.set(pageIndex);
  }

  handleAdd() {
    if (this.addForm.invalid || this.submitting()) return;

    this.submitting.set(true);
    const { label, description } = this.addForm.getRawValue();

    this._tagService
      .createTag({
        label,
        description: description || undefined,
      })
      .pipe(
        catchError((error) => {
          const detail = error?.error?.detail;
          if (detail?.errorCode === 'TAG_LABEL_DUPLICATE_INACTIVE') {
            this.addForm.controls.label.setErrors({
              duplicateInactive: detail.metadata?.existingLabel ?? label,
            });
          } else if (detail?.errorCode === 'TAG_LABEL_DUPLICATE') {
            this.addForm.controls.label.setErrors({
              duplicate: detail.metadata?.existingLabel ?? label,
            });
          } else {
            this._dialog.alert({
              title: 'Foutmelding',
              message:
                detail?.errorMessage ??
                'Er is iets misgegaan bij het aanmaken van de tag.',
              confirmText: 'OK',
            });
          }
          return EMPTY;
        }),
        finalize(() => this.submitting.set(false))
      )
      .subscribe({
        next: () => {
          this._snackbar.open('Tag aangemaakt', 'OK');
          this.showAddForm.set(false);
        },
      });
  }

  handleEdit() {
    const tagId = this.editingTagId();
    if (!tagId || this.editForm.invalid || this.submitting()) return;

    this.submitting.set(true);
    const { label, description } = this.editForm.getRawValue();

    this._tagService
      .updateTag(tagId, {
        label,
        description: description || undefined,
      })
      .pipe(
        catchError((error) => {
          const detail = error?.error?.detail;
          if (detail?.errorCode === 'TAG_LABEL_DUPLICATE') {
            this.editForm.controls.label.setErrors({
              duplicate: detail.metadata?.existingLabel ?? label,
            });
          } else {
            this._dialog.alert({
              title: 'Foutmelding',
              message:
                detail?.errorMessage ??
                'Er is iets misgegaan bij het bijwerken van de tag.',
              confirmText: 'OK',
            });
          }
          return EMPTY;
        }),
        finalize(() => this.submitting.set(false))
      )
      .subscribe({
        next: () => {
          this._snackbar.open('Tag bijgewerkt', 'OK');
          this.editingTagId.set(null);
        },
      });
  }

  handleDelete(t: TagResponse) {
    this._tagService.getTagRefCount(t.id).subscribe((result) => {
      const { usages, scmBlocks } = result;
      const hasRefs = usages > 0 || scmBlocks > 0;

      let refInfo = '';
      if (hasRefs) {
        const parts: string[] = [];
        if (usages > 0) {
          parts.push(
            `<b>${usages}</b> interactie${usages === 1 ? '' : 's'} gekoppeld via tag-referenties`
          );
        }
        if (scmBlocks > 0) {
          parts.push(
            `<b>${scmBlocks}</b> signalerings-blok${scmBlocks === 1 ? '' : 'ken'}`
          );
        }
        refInfo = ` Deze tag heeft ${parts.join(' en ')}.`;
      }

      this._dialog.confirm({
        title: 'Tag verwijderen',
        message:
          `Weet je zeker dat je de tag <b>${escapeHtml(t.label)}</b> wilt verwijderen?${refInfo} ` +
          `De tag wordt gedeactiveerd en na <b>10 dagen</b> definitief verwijderd, ` +
          `inclusief alle koppelingen en bijbehorende signalerings-blokken. ` +
          `Tijdens deze periode kan de tag nog worden hersteld.`,
        confirmText: 'Verwijderen',
        cancelText: 'Annuleren',
        onConfirm: () => this._performDelete(t.id),
      });
    });
  }

  handleRestore(t: TagResponse) {
    this._dialog.confirm({
      title: 'Tag herstellen',
      message: `Wil je de tag <b>${escapeHtml(t.label)}</b> herstellen? De tag wordt weer actief en beschikbaar.`,
      confirmText: 'Herstellen',
      cancelText: 'Annuleren',
      onConfirm: () => this._performRestore(t.id, t.label),
    });
  }

  togglePending() {
    this.pendingExpanded.update((v) => !v);
  }

  toggleInactive() {
    this.inactiveExpanded.update((v) => !v);
  }

  handleDeactivate(t: TagResponse) {
    this._dialog.confirm({
      title: 'Tag deactiveren',
      message: `Weet je zeker dat je de tag <b>${escapeHtml(t.label)}</b> wilt deactiveren? De tag blijft bestaan maar kan niet meer worden geselecteerd of bewerkt.`,
      confirmText: 'Deactiveren',
      cancelText: 'Annuleren',
      onConfirm: () =>
        this._tagService.deactivateTag(t.id).pipe(
          catchError(() => {
            this._dialog.alert({
              title: 'Foutmelding',
              message: 'Er is iets misgegaan bij het deactiveren van de tag.',
              confirmText: 'OK',
            });
            return EMPTY;
          })
        ),
    });
  }

  handleActivate(t: TagResponse) {
    this._dialog.confirm({
      title: 'Tag activeren',
      message: `Wil je de tag <b>${escapeHtml(t.label)}</b> weer activeren? De tag wordt weer selecteerbaar en bewerkbaar.`,
      confirmText: 'Activeren',
      cancelText: 'Annuleren',
      onConfirm: () =>
        this._tagService.activateTag(t.id).pipe(
          catchError(() => {
            this._dialog.alert({
              title: 'Foutmelding',
              message: 'Er is iets misgegaan bij het activeren van de tag.',
              confirmText: 'OK',
            });
            return EMPTY;
          })
        ),
    });
  }

  handleDeleteInactive(t: TagResponse) {
    this._tagService.getTagRefCount(t.id).subscribe((result) => {
      const { usages, scmBlocks } = result;
      const hasRefs = usages > 0 || scmBlocks > 0;

      let refInfo = '';
      if (hasRefs) {
        const parts: string[] = [];
        if (usages > 0) {
          parts.push(
            `<b>${usages}</b> interactie${usages === 1 ? '' : 's'} gekoppeld via tag-referenties`
          );
        }
        if (scmBlocks > 0) {
          parts.push(
            `<b>${scmBlocks}</b> signalerings-blok${scmBlocks === 1 ? '' : 'ken'}`
          );
        }
        refInfo = ` Deze tag heeft ${parts.join(' en ')}.`;
      }

      this._dialog.confirm({
        title: 'Inactieve tag verwijderen',
        message:
          `Weet je zeker dat je de inactieve tag <b>${escapeHtml(t.label)}</b> wilt verwijderen?${refInfo} ` +
          `De tag wordt na <b>10 dagen</b> definitief verwijderd.`,
        confirmText: 'Verwijderen',
        cancelText: 'Annuleren',
        onConfirm: () => this._performDelete(t.id),
      });
    });
  }

  handleSort({ active, direction }: Sort) {
    this.sortActive.set(active);
    this.sortDirection.set(direction);
    this.page.set(0);
  }

  handlePendingSort({ active, direction }: Sort) {
    this.pendingSortActive.set(active);
    this.pendingSortDirection.set(direction);
  }

  private _sortTags(
    tags: TagResponse[],
    active: string,
    direction: 'asc' | 'desc' | ''
  ): TagResponse[] {
    if (!active || !direction) {
      return [...tags].sort((a, b) => a.label.localeCompare(b.label));
    }

    const multiplier = direction === 'asc' ? 1 : -1;
    return [...tags].sort((a, b) => {
      switch (active) {
        case 'label':
          return multiplier * a.label.localeCompare(b.label);
        case 'deleteAt':
          return (
            multiplier *
            (new Date(a.deleteAt ?? 0).getTime() - new Date(b.deleteAt ?? 0).getTime())
          );
        default:
          return 0;
      }
    });
  }

  formatDeleteAt(isoDate: string): string {
    const date = new Date(isoDate);
    return date.toLocaleDateString('nl-NL', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  }

  private _performDelete(tagId: string) {
    return this._tagService.deleteTag(tagId).pipe(
      catchError(() => {
        this._dialog.alert({
          title: 'Foutmelding',
          message: 'Er is iets misgegaan bij het verwijderen van de tag.',
          confirmText: 'OK',
        });
        return EMPTY;
      })
    );
  }

  private _performRestore(tagId: string, tagLabel: string) {
    return this._tagService.restoreTag(tagId).pipe(
      catchError((error) => {
        const detail = error?.error?.detail;
        const isDuplicate = detail?.errorCode === 'TAG_LABEL_DUPLICATE';
        this._dialog.alert({
          title: isDuplicate ? 'Kan niet herstellen' : 'Foutmelding',
          message: isDuplicate
            ? `Er bestaat al een actieve tag met het label "${escapeHtml(detail.metadata?.existingLabel ?? tagLabel)}". Hernoem of verwijder die tag eerst.`
            : 'Er is iets misgegaan bij het herstellen van de tag.',
          confirmText: 'OK',
        });
        return EMPTY;
      })
    );
  }
}
